import type { ReactNode } from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'

/**
 * The Wellday primitive set.
 *
 * Deliberately small and in one file: a handful of composable pieces used by all
 * four portals, so the visual language stays identical whether a Wellday admin or
 * a station manager is looking at it. Everything reads the semantic tokens from
 * globals.css — no component hard-codes a brand colour.
 */

// ── Layout ──────────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
}: {
  title: string
  subtitle?: ReactNode
  breadcrumbs?: Array<{ label: string; href?: string }>
  actions?: ReactNode
}) {
  return (
    <div className="mb-7">
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav aria-label="Brödsmulor" className="mb-2.5">
          <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-[var(--text-faint)]">
            {breadcrumbs.map((crumb, i) => (
              <li key={`${crumb.label}-${i}`} className="flex items-center gap-1.5">
                {i > 0 ? <span aria-hidden>/</span> : null}
                {crumb.href ? (
                  <Link href={crumb.href} className="transition-colors hover:text-[var(--text-primary)]">
                    {crumb.label}
                  </Link>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-[26px] leading-tight font-semibold tracking-[-0.02em]">{title}</h1>
          {subtitle ? (
            <p className="mt-1.5 max-w-2xl text-[14px] leading-relaxed text-[var(--text-secondary)]">{subtitle}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  )
}

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode
  className?: string
  padded?: boolean
}) {
  return <section className={cn('surface-card', padded && 'p-5', className)}>{children}</section>
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h2 className="font-display text-[15px] font-semibold tracking-[-0.01em]">{title}</h2>
        {description ? (
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--text-secondary)]">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

export function Grid({ cols = 3, children }: { cols?: 2 | 3 | 4; children: ReactNode }) {
  const map = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  } as const
  return <div className={cn('grid grid-cols-1 gap-4', map[cols])}>{children}</div>
}

// ── Data display ────────────────────────────────────────────────────────────

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  href,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: Tone
  href?: string
}) {
  const body = (
    <>
      <div className="text-[12px] font-medium tracking-[0.02em] text-[var(--text-secondary)]">{label}</div>
      <div
        className={cn(
          'tabular mt-2 font-display text-[30px] leading-none font-semibold tracking-[-0.02em]',
          tone !== 'neutral' && TONE_TEXT[tone],
        )}
      >
        {value}
      </div>
      {hint ? <div className="mt-2 text-[12px] leading-snug text-[var(--text-faint)]">{hint}</div> : null}
    </>
  )

  if (href) {
    return (
      <Link
        href={href}
        className="surface-card block p-5 transition-shadow hover:shadow-[0_2px_4px_rgb(11_15_18/0.06),0_12px_32px_-12px_rgb(11_15_18/0.18)]"
      >
        {body}
      </Link>
    )
  }
  return <div className="surface-card p-5">{body}</div>
}

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info'

const TONE_TEXT: Record<Exclude<Tone, 'neutral'>, string> = {
  ok: 'text-[var(--color-ok)]',
  warn: 'text-[var(--color-warn)]',
  danger: 'text-[var(--color-danger)]',
  info: 'text-[var(--color-info)]',
}

const TONE_BADGE: Record<Tone, string> = {
  neutral: 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
  ok: 'bg-[var(--color-ok-soft)] text-[var(--color-ok)]',
  warn: 'bg-[var(--color-warn-soft)] text-[var(--color-warn)]',
  danger: 'bg-[var(--color-danger-soft)] text-[var(--color-red-deep)]',
  info: 'bg-[var(--color-info-soft)] text-[var(--color-info)]',
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: Tone
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium whitespace-nowrap',
        TONE_BADGE[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/** A small coloured dot — used in dense tables where a full badge is too heavy. */
export function Dot({ tone = 'neutral' }: { tone?: Tone }) {
  const map: Record<Tone, string> = {
    neutral: 'bg-[var(--color-sand-dark)]',
    ok: 'bg-[var(--color-ok)]',
    warn: 'bg-[var(--color-warn)]',
    danger: 'bg-[var(--color-danger)]',
    info: 'bg-[var(--color-info)]',
  }
  return <span className={cn('inline-block size-2 shrink-0 rounded-full', map[tone])} aria-hidden />
}

/** Horizontal fill bar: on-hand against par level. */
export function LevelBar({
  value,
  max,
  min,
  tone,
}: {
  value: number
  max: number
  min?: number
  tone?: Tone
}) {
  const safeMax = Math.max(max, 1)
  const pct = Math.min(100, Math.round((value / safeMax) * 100))
  const resolved: Tone = tone ?? (value === 0 ? 'danger' : min !== undefined && value <= min ? 'warn' : 'ok')
  const fill: Record<Tone, string> = {
    neutral: 'bg-[var(--color-sand-dark)]',
    ok: 'bg-[var(--color-ok)]',
    warn: 'bg-[var(--color-warn)]',
    danger: 'bg-[var(--color-danger)]',
    info: 'bg-[var(--color-info)]',
  }
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-sunken)]"
      role="img"
      aria-label={`${value} av ${max}`}
    >
      <div className={cn('h-full rounded-full transition-[width]', fill[resolved])} style={{ width: `${pct}%` }} />
    </div>
  )
}

// ── Table ───────────────────────────────────────────────────────────────────

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  // Wide tables scroll inside their own container; the page body never scrolls sideways.
  return (
    <div className="scroll-x -mx-5 px-5">
      <table className={cn('w-full min-w-[640px] border-collapse text-[13.5px]', className)}>{children}</table>
    </div>
  )
}

export function Th({
  children,
  align = 'left',
  className,
}: {
  children?: ReactNode
  align?: 'left' | 'right' | 'center'
  className?: string
}) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-[var(--line-hairline)] pb-2.5 text-[11.5px] font-semibold tracking-[0.04em] whitespace-nowrap text-[var(--text-faint)] uppercase',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className,
      )}
    >
      {children}
    </th>
  )
}

export function Td({
  children,
  align = 'left',
  className,
  numeric,
}: {
  children?: ReactNode
  align?: 'left' | 'right' | 'center'
  className?: string
  numeric?: boolean
}) {
  return (
    <td
      className={cn(
        'border-b border-[var(--line-hairline)] py-2.5 align-middle',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        numeric && 'tabular',
        className,
      )}
    >
      {children}
    </td>
  )
}

export function Tr({ children, className }: { children: ReactNode; className?: string }) {
  return <tr className={cn('transition-colors hover:bg-[var(--surface-sunken)]/50', className)}>{children}</tr>
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-card)] border border-dashed border-[var(--line-hairline)] px-6 py-12 text-center">
      <p className="font-display text-[15px] font-semibold">{title}</p>
      {description ? (
        <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-[var(--text-secondary)]">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

// ── Controls ────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] shadow-[0_1px_2px_rgb(11_15_18/0.10)]',
  secondary:
    'bg-[var(--surface-raised)] text-[var(--text-primary)] border border-[var(--line-strong)] hover:bg-[var(--surface-sunken)]',
  ghost: 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]',
  danger: 'bg-[var(--color-red-deep)] text-white hover:brightness-110',
}

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] px-3.5 py-2 text-[13.5px] font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50'

export function Button({
  children,
  variant = 'secondary',
  className,
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button type={type} className={cn(BUTTON_BASE, BUTTON_VARIANTS[variant], className)} {...props}>
      {children}
    </button>
  )
}

export function LinkButton({
  children,
  href,
  variant = 'secondary',
  className,
}: {
  children: ReactNode
  href: string
  variant?: ButtonVariant
  className?: string
}) {
  return (
    <Link href={href} className={cn(BUTTON_BASE, BUTTON_VARIANTS[variant], className)}>
      {children}
    </Link>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
  htmlFor?: string
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-medium text-[var(--text-primary)]">
        {label}
      </label>
      {children}
      {hint && !error ? <p className="text-[12px] text-[var(--text-faint)]">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-[12px] text-[var(--color-red-deep)]">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export const inputClass =
  'w-full rounded-[var(--radius-control)] border border-[var(--line-strong)] bg-[var(--surface-raised)] px-3 py-2 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-faint)] transition-colors focus:border-[var(--accent)] focus:outline-none'

/** Key/value list used on every detail page. */
export function DescriptionList({
  items,
  columns = 2,
}: {
  items: Array<{ label: string; value: ReactNode }>
  columns?: 1 | 2 | 3
}) {
  const map = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' } as const
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-4', map[columns])}>
      {items.map((item) => (
        <div key={item.label}>
          <dt className="text-[11.5px] font-semibold tracking-[0.04em] text-[var(--text-faint)] uppercase">
            {item.label}
          </dt>
          <dd className="mt-1 text-[14px] text-[var(--text-primary)]">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Horizontal tab strip for detail pages. Server-rendered links, not client state. */
export function TabNav({
  tabs,
  active,
}: {
  tabs: Array<{ label: string; href: string; key: string; count?: number }>
  active: string
}) {
  return (
    <div className="scroll-x rule-hairline mb-6 flex gap-1 border-b">
      {tabs.map((tab) => {
        const isActive = tab.key === active
        return (
          <Link
            key={tab.key}
            href={tab.href}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              '-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-[13.5px] font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'border-[var(--accent)] text-[var(--text-primary)]'
                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="tabular rounded-full bg-[var(--surface-sunken)] px-1.5 py-0.5 text-[11px] text-[var(--text-faint)]">
                {tab.count}
              </span>
            ) : null}
          </Link>
        )
      })}
    </div>
  )
}
